import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { listRegions, listZones, type RegionRow, type ZoneRow } from '../../api/orgApi.js';
import { DataGrid } from '../../components/DataGrid.js';
import { ListShell, StatusBadge, sx } from './orgKit.js';

export function RegionListPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<RegionRow[]>([]);
  const [zones, setZones] = useState<ZoneRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([listRegions(token), listZones(token)])
      .then(([r, z]) => {
        setRows(r.data);
        setZones(z.data);
      })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [token]);

  const zoneName = (id: string): string => zones.find((z) => z.zoneId === id)?.zoneName ?? '—';

  return (
    <ListShell
      label="region-list"
      title={t('org.region.list.title')}
      newTo="/org/regions/new"
      newLabel={t('org.region.list.new')}
      error={error}
      loading={loading}
      empty={!loading && rows.length === 0}
      emptyText={t('org.region.list.empty')}
    >
      {rows.length > 0 && (
        <DataGrid<RegionRow>
          caption="regions"
          rows={rows}
          getRowId={(r) => r.regionId}
          rowTestId={(r) => `region-${r.regionId}`}
          searchPlaceholder={t('org.common.searchNameCode')}
          columns={[
            {
              key: 'code',
              header: t('org.col.code'),
              value: (r) => r.regionCode,
              render: (r) => (
                <Link to={`/org/regions/${r.regionId}/edit`} style={sx.link}>{r.regionCode}</Link>
              ),
            },
            { key: 'name', header: t('org.col.name'), value: (r) => r.regionName },
            { key: 'zone', header: t('org.col.zone'), value: (r) => zoneName(r.parentZoneId) },
            {
              key: 'status',
              header: t('org.col.status'),
              value: (r) => r.recordStatusCode,
              render: (r) => <StatusBadge status={r.recordStatusCode} />,
            },
          ]}
        />
      )}
    </ListShell>
  );
}
